import React, { useState, useEffect } from "react";
import { filterCaseStudiesBySection } from "../utils";
import partnershipMarketPlace from "../assets/images/partnership-microsoft-azure-marketplace.png";
import BulletCardList from "../components/common/BulletCard/BulletCardList";
import Carousel from "../components/sections/Carousel";
import databricksPartnerLogo from "../assets/images/databricks-partner-logo.png";
import {
  ComputerDesktopIcon,
  Square3Stack3DIcon,
  ArrowsPointingOutIcon,
  Cog6ToothIcon,
  CloudArrowDownIcon,
} from "@heroicons/react/24/outline";

const Databricks = () => {
  const [selectedCaseStudies, setSelectedCaseStudies] = useState([]);
  const services = [
    {
      icon: ComputerDesktopIcon,
      label: "Unity Catalog Readiness & Implementation",
      description:
        "Standardize data access, compliance, auditability, and lineage across Databricks environments.",
    },
    {
      icon: Square3Stack3DIcon,
      label: "Lakehouse Architecture & Modernization",
      description:
        "Unify analytics, BI, machine learning, batch, and streaming workloads on a modern cloud-native data platform.",
    },
    {
      icon: ArrowsPointingOutIcon,
      label: "Databricks Deployment Automation",
      description:
        "Accelerate delivery and reduce configuration risk with automated Databricks provisioning using Terraform and CI/CD.",
    },
    {
      icon: Cog6ToothIcon,
      label: "Secure Data Pipeline Engineering",
      description:
        "Build secure, governed, and observable data workflows across the full software and data delivery lifecycle.",
    },
    // {
    //   icon: CloudArrowDownIcon,
    //   label: "Databricks + BuildX Talent Development",
    //   description:
    //     "Deliver Databricks solutions while building long-term internal capability through mentored engineering pods and practical knowledge transfer.",
    // },
  ];

  const azureOfferingValues = [
    {
      icon: CloudArrowDownIcon,
      img: null,
      img_link: null,
      label: "Unity Catalog Readiness & Implementation",
      description:
        "Design and implement enterprise-wide governance with fine-grained access control, centralized auditing, lineage, and scalable catalog structures.",
    },
    {
      icon: Square3Stack3DIcon,
      img: null,
      img_link: null,
      label: "Lakehouse Architecture & Modernization",
      description:
        "Create a future-ready data platform that brings together structured and semi-structured data for analytics, AI, ML, and operational reporting.",
    },
    {
      icon: ComputerDesktopIcon,
      img: partnershipMarketPlace,
      img_link: null,
      label: "Databricks Deployment Automation",
      description:
        "Use repeatable infrastructure patterns to provision workspaces, compute, storage, catalogs, schemas, volumes, and access controls consistently across environments.",
    },
    {
      icon: Cog6ToothIcon,
      img: partnershipMarketPlace,
      img_link: null,
      label: "Secure Data Pipeline Engineering",
      description:
        "Design and deliver reliable batch and real-time pipelines using Spark, Delta Lake, Delta Live Tables, Kafka, and modern orchestration practices.",
    },
    {
      icon: CloudArrowDownIcon,
      img: partnershipMarketPlace,
      img_link: null,
      label: "Databricks + BuildX Talent Development",
      description:
        "Deliver Databricks solutions while building long-term internal capability through mentored engineering pods and practical knowledge transfer.",
    },
  ];

  useEffect(() => {
    const databricksCaseStudies = filterCaseStudiesBySection("databricks");
    setSelectedCaseStudies(databricksCaseStudies);
  }, []);

  return (
    <div className="container mx-auto pt-8 px-6 sm:px-12 md:px-24 lg:px-28">
      <div className="my-6">
        <h1 className="text-4xl font-bold mb-10 text-gray-900 sm:text-5xl">
          Our <span className="text-purple-700">Databricks</span> Partnership
          and Services
        </h1>
        <div className="flex flex-row flex-wrap lg:flex-nowrap justify-center gap-4">
          <div className="bg-white rounded-lg shadow-md p-12">
            <img
              src={databricksPartnerLogo}
              alt="Databricks Partner Logo"
              className="w-full max-w-xs lg:max-w-sm h-full"
            />
          </div>
        </div>
        <p className="mt-14">
          RAVL helps highly regulated organizations design, govern, automate,
          and scale modern Databricks platforms. We combine strategic advisory,
          hands-on engineering, and sustainable talent development to help teams
          unlock the full potential of the Data Lakehouse.
        </p>
      </div>

      <div className="my-14">
        <h3 className="text-lg sm:text-xl font-semibold text-purple-700">
          Our Databricks Services
        </h3>
        <p className="mt-2">
          At RAVL, we offer a complete range of Databricks consulting services
          to help you unlock the full potential of Databricks and achieve your
          business goals.
        </p>
      </div>
      <div className="my-14">
        <BulletCardList values={services} />
      </div>

      <div className="my-14">
        <h3 className="text-lg sm:text-xl font-semibold text-purple-700">
          Our Databricks Offerings
        </h3>
        <div className="mt-2">
          {azureOfferingValues.map((offering, index) => (
            <div
              className={`card text-left max-w-3xl self-center my-8 ${index % 2 ? "ml-auto" : "mr-auto"}`}
              key={index}
            >
              <div className="flex flex-col md:flex-row items-start gap-4">
                <div className="flex-grow flex flex-col justify-center">
                  <div className="flex items-center gap-4">
                    {offering.icon && (
                      <offering.icon
                        aria-hidden="true"
                        focusable="false"
                        className="w-6 h-6 stroke-purple-500 fill-transparent"
                      />
                    )}
                    <h4 className="font-semibold text-purple-700">
                      {offering.label}
                    </h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    {offering.description}
                  </p>
                </div>
                {offering.img && offering.img_link && (
                  <a
                    href={offering.img_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-0 md:w-auto w-full"
                  >
                    <img
                      src={offering.img}
                      alt="Azure Offering"
                      className="w-3/4 md:w-1/4 lg:w-full h-auto mx-auto md:mx-0"
                    />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="my-14">
        <h3 className="text-lg sm:text-xl font-semibold text-purple-700">
          Why Choose RAVL?
        </h3>
        <p className="mt-2">
          RAVL brings deep engineering experience, governance-first delivery,
          and practical execution discipline to Databricks initiatives. We help
          organizations move from strategy to production with secure platforms,
          repeatable deployment patterns, and teams that can sustain the work
          after delivery.
        </p>
      </div>
      <div className="my-14">
        <h3 className="text-lg sm:text-xl font-semibold text-purple-700 mb-8">
          The Work We've Done:
        </h3>
        <Carousel caseStudies={selectedCaseStudies} />
        <div className="my-14">
          <h3 className="text-lg sm:text-xl font-semibold text-purple-700 mt-10">
            Let's Talk!
          </h3>
          <p className="mt-2">
            Ready to modernize your Databricks platform? Contact RAVL to explore
            how we can help you build a secure, governed, and scalable Data
            Lakehouse.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Databricks;
